import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Chip,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import tw from 'twin.macro';

import ChevronIcon from '../../../assets/svg/ChevronIcon';
import CrossIcon from '../../../assets/svg/CrossIcon';
import {
  REPORT_FILE_FORMAT,
  REPORT_FORMAT_OPTIONS,
  VALIDATION_SCHEMAS,
} from '../../../constants';
import { BasicModal, Button, ErrorMessage } from '../../Shared';

const StyledSelect = styled(Select)`
  &.MuiInputBase-root {
    ${tw`px-4 h-12`}

    .MuiInputBase-input {
      ${tw`text-[18px]/[24px] text-base-1000 p-0`}
    }

    & .MuiSelect-select {
      ${tw`min-h-6`}
    }

    .MuiOutlinedInput-notchedOutline {
      ${tw`border rounded-xl border-brand-300/30`}
    }

    &:hover {
      & .MuiOutlinedInput-notchedOutline {
        ${tw`border-brand-300/50 bg-brand-300/[0.08]`}
      }
    }
  }

  &.Mui-focused {
    .MuiOutlinedInput-notchedOutline {
      ${tw`!border !border-[#9747FF]`}
    }
  }

  &.Mui-error {
    .MuiOutlinedInput-notchedOutline {
      ${tw`!border !border-destructive-500`}
    }
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    ${tw`px-4 py-2.5 min-h-12 flex gap-1.5`}

    .MuiInputBase-input {
      ${tw`text-[18px]/[24px] text-base-1000 p-0`}

      &::placeholder {
        ${tw`text-base-600`}
      }
    }

    .MuiOutlinedInput-notchedOutline {
      ${tw`border rounded-xl border-brand-300/30`}
    }

    &:hover {
      & .MuiOutlinedInput-notchedOutline {
        ${tw`border-brand-300/50 bg-brand-300/[0.08]`}
      }
    }
  }

  .Mui-focused {
    .MuiOutlinedInput-notchedOutline {
      ${tw`!border !border-[#9747FF]`}
    }
  }

  .Mui-error {
    .MuiOutlinedInput-notchedOutline {
      ${tw`!border !border-destructive-500`}
    }
  }
`;

const StyledInputLabel = styled(InputLabel)`
  ${tw`text-[16px]/[24px] text-base-800 font-medium`}
`;

const StyledMenuItem = styled(MenuItem)`
  &.MuiButtonBase-root {
    ${tw`text-[18px]/[24px] text-base-1000 px-3 py-2 rounded-lg`}

    &:hover {
      ${tw`bg-brand-300/[0.08]`}
    }

    &:focus {
      ${tw`!bg-inherit`}
    }
  }

  &.Mui-selected {
    ${tw`!bg-inherit`}

    &:hover {
      ${tw`!bg-brand-300/[0.08]`}
    }
  }

  &.Mui-focused {
    ${tw`!bg-inherit`}
  }
`;

const StyledChip = styled(Chip)`
  ${tw`!m-0 border-none h-auto py-1 pl-3 pr-2`}

  &.MuiChip-colorDefault {
    ${tw`bg-brand-100`}

    & .MuiChip-label {
      ${tw`w-full p-0 pr-1 text-[14px]/[20px] text-base-1000 font-medium`}
    }

    & .MuiChip-deleteIcon {
      ${tw`m-0 text-brand-300`}

      &:hover {
        ${tw`text-brand-300`}
      }
    }
  }

  &.MuiChip-colorError {
    ${tw`bg-destructive-500/30`}

    & .MuiChip-label {
      ${tw`w-full p-0 pr-1 text-[14px]/[20px] text-destructive-500 font-medium`}
    }

    & .MuiChip-deleteIcon {
      ${tw`m-0 text-destructive-500`}

      &:hover {
        ${tw`text-destructive-500`}
      }
    }
  }
`;

export const SendReportModal = ({
  open,
  disabled,
  onSubmit,
  onClose,
  options = REPORT_FORMAT_OPTIONS,
}) => {
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(VALIDATION_SCHEMAS.SEND_REPORT),
    defaultValues: {
      reportFormat: REPORT_FILE_FORMAT.CSV,
      sendTo: [],
      subject: '',
      message: '',
    },
  });

  return (
    <BasicModal open={open} onClose={disabled ? undefined : onClose}>
      <div className="bg-white w-[560px] rounded-2xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <div className="flex items-center justify-between py-4 pl-5 pr-4 border-b border-b-base-200">
          <span className="text-base-1000 font-semibold text-[22px]/[28px]">
            Send report
          </span>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
            data-hs-overlay="#edit-contact-modal"
            onClick={onClose}
            disabled={disabled}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-4 w-4 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col gap-6 py-4 px-5">
          <div className="flex flex-col gap-1">
            <StyledInputLabel id="reportFormat">Report Format</StyledInputLabel>
            <Controller
              control={control}
              name="reportFormat"
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                const hasErrorMessage = !!error?.message;

                return (
                  <div className="relative">
                    <StyledSelect
                      labelId="reportFormat"
                      value={value}
                      onChange={onChange}
                      fullWidth
                      error={hasErrorMessage}
                      MenuProps={{
                        PaperProps: {
                          className:
                            '!rounded-xl !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)]',
                        },
                        sx: {
                          '& .MuiList-root': {
                            padding: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          },
                        },
                      }}
                      IconComponent={({ className }) => (
                        <ChevronIcon
                          className={twMerge(
                            className,
                            'w-5 h-5 !text-brand-1000 !right-3.5 !top-3.5',
                          )}
                        />
                      )}
                    >
                      {options.map(({ value, label, disabled }) => (
                        <StyledMenuItem
                          key={value}
                          value={value}
                          disabled={disabled}
                          disableRipple
                        >
                          {label}
                        </StyledMenuItem>
                      ))}
                    </StyledSelect>
                    {hasErrorMessage && (
                      <ErrorMessage
                        errorMessage={error.message}
                        className="absolute"
                      />
                    )}
                  </div>
                );
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <StyledInputLabel htmlFor="sendTo">Send to</StyledInputLabel>
            <Controller
              control={control}
              name="sendTo"
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                const hasError = Boolean(error);
                const isArrayError = Array.isArray(error);
                const itemError = isArrayError
                  ? error.find(e => !!e?.message)
                  : undefined;
                const hasErrorMessage =
                  !!error?.message || !!itemError?.message;
                const isErrorMessageVisible = hasError && hasErrorMessage;
                const errorMessage = error?.message ?? itemError?.message;

                return (
                  <div className="relative">
                    <Autocomplete
                      id="sendTo"
                      value={value}
                      onChange={(_e, nextValue) => onChange(nextValue)}
                      multiple
                      freeSolo
                      disableClearable
                      options={[]}
                      filterOptions={() => []}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const hasChipError = !!error?.[index];
                          const color = hasChipError ? 'error' : 'default';
                          const { key, ...tagProps } = getTagProps({ index });

                          return (
                            <StyledChip
                              key={key}
                              variant="outlined"
                              label={option}
                              deleteIcon={<CrossIcon className="w-4 h-4" />}
                              color={color}
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={params => (
                        <StyledTextField
                          {...params}
                          variant="outlined"
                          placeholder="Type and press Enter"
                          error={hasError}
                        />
                      )}
                    />
                    {isErrorMessageVisible && (
                      <ErrorMessage
                        errorMessage={errorMessage}
                        className="absolute"
                      />
                    )}
                  </div>
                );
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <StyledInputLabel htmlFor="subject">Subject</StyledInputLabel>
            <Controller
              control={control}
              name="subject"
              render={({ field: { value, onChange } }) => (
                <StyledTextField
                  id="subject"
                  value={value}
                  onChange={onChange}
                  placeholder="Subject"
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-1">
            <StyledInputLabel htmlFor="message">Message</StyledInputLabel>
            <Controller
              control={control}
              name="message"
              render={({ field: { value, onChange } }) => (
                <StyledTextField
                  id="message"
                  value={value}
                  onChange={onChange}
                  multiline
                  rows={5}
                  placeholder="Message"
                />
              )}
            />
          </div>
        </div>
        <div className="flex justify-end py-4 px-5 border-t border-t-base-200 gap-3">
          <Button
            title="Cancel"
            variant="blue"
            onClick={onClose}
            disabled={disabled}
          />
          <Button
            title="Send"
            onClick={handleSubmit(onSubmit)}
            disabled={disabled}
          />
        </div>
      </div>
    </BasicModal>
  );
};
